import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Server, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      // Direct fetch to avoid AuthContext complexity for unauth request
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'email': resetEmail }
      });
      if (res.ok) {
        toast.success('Reset link sent (if email exists)');
        setShowReset(false);
        setResetEmail('');
      } else {
        toast.error('Failed to send reset link');
      }
    } catch (err) {
      toast.error('Error sending reset request');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(password);
      toast.success('Authenticated');
    } catch (err) {
      const msg = err.response?.data?.detail
        || (!err.response && err.request ? 'Backend unreachable (API URL / proxy misconfigured)' : null)
        || 'Authentication failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="login-page">
      {/* Background */}
      <div className="absolute inset-0 bg-[#09090b]">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1759159347827-de3a54002de7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzEwNzU0NjF8MA&ixlib=rb-4.1.0&q=85")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(9,9,11,1) 0%, rgba(9,9,11,0.7) 100%)' }} />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6 animate-fade-in-up">
        <div className="bg-[#09090b]/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-8 md:p-10">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-[#EF694C] flex items-center justify-center shadow-[0_0_20px_rgba(239,105,76,0.3)] overflow-hidden">
              <img src="/logo.webp" alt="FyreServe Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-heading text-white tracking-tight">FyreServe</h1>
              <p className="text-sm text-[#a1a1aa]">Server Command Center</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!showReset ? (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#a1a1aa] uppercase tracking-wider">
                    Admin Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a1a1aa]" />
                    <Input
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="h-12 pl-10 bg-[#18181b] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-[#F43F5E] bg-[#F43F5E]/10 border border-[#F43F5E]/20 rounded-lg px-4 py-3" data-testid="login-error">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading || !password}
                  className="w-full h-12 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(239,105,76,0.3)] hover:shadow-[0_0_25px_rgba(239,105,76,0.5)] transition-all duration-200 active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Authenticating...' : 'Authenticate'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowReset(true)}
                    className="text-xs text-[#a1a1aa] hover:text-[#EF694C] transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4 animate-fade-in-up">
                <div className="text-center mb-4">
                   <h3 className="text-white font-semibold">Reset Password</h3>
                   <p className="text-xs text-[#a1a1aa]">Enter your email to receive a reset link.</p>
                </div>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="j.krziwanie@onfyre.de"
                  className="h-10 bg-[#18181b] border-white/10 text-white"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowReset(false)}
                    className="flex-1 bg-transparent border-white/10 text-white hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleReset}
                    disabled={resetLoading || !resetEmail}
                    className="flex-1 bg-[#EF694C] hover:bg-[#d65d43] text-white"
                  >
                    {resetLoading ? 'Sending...' : 'Send Link'}
                  </Button>
                </div>
              </div>
            )}
          </form>

          <p className="mt-6 text-xs text-center text-[#52525b]">
            Private dashboard. Authorized access only.
          </p>
        </div>
      </div>
    </div>
  );
}
